import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import AkceServiceApi, { type SearchAkceParams } from "./AkceService";
import type { Akce, AkceDetail, AkceOdpoved, CreateAkceRequest, UpdateAkceRequest, PagedResponseAkce } from "../generated/models/Akce";

// -----------------------------
// AKCE QUERIES
// -----------------------------

export const useSearchAkceQuery = (params: SearchAkceParams) => {
    return useQuery<PagedResponseAkce, Error>({
        queryKey: ["akce", params],
        queryFn: () => AkceServiceApi.searchAkce(params),
        staleTime: 30 * 1000,
    });
};

export const useAkceDetailQuery = (id: number | null) => {
    return useQuery<AkceDetail, Error>({
        queryKey: ["akce", "detail", id],
        queryFn: () => AkceServiceApi.getAkceDetail(id!),
        enabled: id !== null,
        staleTime: 15 * 1000,
    });
};

export const useNadchazejiciAkceQuery = (days: number = 30) => {
    return useQuery<Akce[], Error>({
        queryKey: ["akce", "nadchazejici", days],
        queryFn: () => AkceServiceApi.getNadchazejiciAkce(days),
        staleTime: 60 * 1000,
    });
};

// -----------------------------
// AKCE MUTATIONS
// -----------------------------

export const useCreateAkceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<Akce, Error, CreateAkceRequest>({
        mutationFn: (data) => AkceServiceApi.createAkce(data),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["akce"] });
            showToast("Akce byla úspěšně vytvořena.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vytvořit akci.", "error");
        },
    });
};

export const useUpdateAkceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<Akce, Error, { id: number; data: UpdateAkceRequest }>({
        mutationFn: ({ id, data }) => AkceServiceApi.updateAkce(id, data),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["akce"] });
            showToast("Akce byla úspěšně aktualizována.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat akci.", "error");
        },
    });
};

export const useDeleteAkceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (id) => AkceServiceApi.deleteAkce(id),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["akce"] });
            showToast("Akce byla úspěšně smazána.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat akci.", "error");
        },
    });
};

export const useSubmitOdpovedMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<AkceOdpoved, Error, { akceId: number; odpoved: 'BUDU' | 'NEBUDU' }>({
        mutationFn: ({ akceId, odpoved }) => AkceServiceApi.submitOdpoved(akceId, odpoved),

        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["akce", "detail", variables.akceId] });
            showToast("Odpověď byla uložena.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se uložit odpověď.", "error");
        },
    });
};

export const useSendAkceNotifikaceMutation = () => {
    const { showToast } = useToast();

    return useMutation<{ success: boolean; odeslano: number; message: string }, Error, number>({
        mutationFn: (akceId) => AkceServiceApi.sendNotifikace(akceId),

        onSuccess: (result) => {
            showToast(result.message || `Odesláno ${result.odeslano} notifikací.`, "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se odeslat notifikace.", "error");
        },
    });
};
